/**
 * Human-readable labels and tag colors for screening question/atom statuses.
 */

import { capitalizeWordsSeperatedByUnderScore } from "../../../utils/stringModifications";

/** Tag colors for a screening status badge. */
export interface ScreeningStatusColors {
  backgroundColor: string;
  textColor: string;
}

/**
 * Resolve stable colors for a screening status.
 *
 * @param status - Question or atom screening status
 * @returns Tag background and text colors
 */
export function screeningStatusColors(status: string): ScreeningStatusColors {
  if (status === "PASS") {
    return { backgroundColor: "#E6F3F0", textColor: "#005D49" };
  }
  if (status === "FAIL") {
    return { backgroundColor: "#FEE2E2", textColor: "#991B1B" };
  }
  if (status === "NOT_EVALUATED") {
    return { backgroundColor: "#F3F4F6", textColor: "#5C5C5C" };
  }
  return { backgroundColor: "#FFF3E0", textColor: "#C24400" };
}

/**
 * Map API screening statuses to evaluation-list copy (Met / Not Met).
 *
 * @param status - Raw screening status from the API
 * @returns Label shown on criterion rows and headers
 */
export function screeningStatusLabel(status: string): string {
  if (status === "PASS") {
    return "Met";
  }
  if (status === "FAIL") {
    return "Not Met";
  }
  if (status === "NOT_EVALUATED") {
    return "Not Evaluated";
  }
  if (status === "Action Pending") {
    return "Action Pending";
  }
  return capitalizeWordsSeperatedByUnderScore(status);
}

/**
 * Copy for an evaluation tab when the stage has no questions to show.
 *
 * @param heading - Tab title (e.g. "Criteria Evaluation")
 * @param status - Stage status from the API when a stage record exists
 * @returns Single user-facing sentence for the empty panel
 */
export function emptyEvaluationStageMessage(
  heading: string,
  status?: string,
): string {
  if (status === "NOT_EVALUATED") {
    if (heading === "Criteria Evaluation") {
      return "Criteria have not been evaluated.";
    }
    if (heading === "Primary Evaluation") {
      return "Primary evaluation has not been completed.";
    }
    return "This evaluation has not been completed.";
  }
  if (!status) {
    return "No screening result is available.";
  }
  return `No criteria are available for this stage (${screeningStatusLabel(status)}).`;
}

/**
 * Question copy for a screening answer: the short protocol label, then the criterion sentence, then the id.
 *
 * A labelled question does not repeat the full criterion. A question with no label
 * still shows that sentence instead of a bare id.
 *
 * @param question - Screening question from the detail payload
 * @returns Text to show in place of a bare question id
 */
export function questionDisplayText(question: {
  label?: string | null;
  question_verbatim_text?: string | null;
  question_id?: string | null;
}): string {
  return (
    question.label?.trim() ||
    question.question_verbatim_text?.trim() ||
    question.question_id?.trim() ||
    ""
  );
}
